import { useState, useEffect, useCallback, useMemo } from 'react';

// Pin one line to the top of a list until the user acts (#1933).
//
// Presentation only. The caller's sorted array stays the source of truth
// (#1845): the caller renders the pinned line first instead of at its sorted
// position, never twice, and nothing here reorders data. The pin ends when the
// caller says so (the next add replaces it; a filter change or reload calls
// release) and, derived here, whenever the line leaves the rendered set
// (reconciled under the Unreconciled filter, deleted, moved). The stored id is
// cleared at that point, so a line does not re-pin when a filter flips back and
// brings it into view again. Nothing persists across reload.
//
// `lines` is the list as rendered (after any filter), each item carrying a
// `lineId`. `pinnedIndex` is the pinned line's index in that list, or -1.
export const usePinnedLine = (lines) => {
  const [pinnedId, setPinnedId] = useState(null);

  const pinnedIndex = useMemo(
    () => (pinnedId == null ? -1 : lines.findIndex((l) => l.lineId === pinnedId)),
    [lines, pinnedId]
  );

  // The line left the rendered set: forget it, do not wait for the next add.
  useEffect(() => {
    if (pinnedId != null && pinnedIndex === -1) setPinnedId(null);
  }, [pinnedId, pinnedIndex]);

  const pin = useCallback((lineId) => setPinnedId(lineId ?? null), []);
  const release = useCallback(() => setPinnedId(null), []);

  return {
    pinnedLineId: pinnedIndex === -1 ? null : pinnedId,
    pinnedIndex,
    pin,
    release,
  };
};
